import { FunctionComponent, useEffect, useState } from 'react'
import styles from './groupUrls.module.scss'
import { useTranslation } from 'react-i18next'
import { DaycareGroup, DaycareGroupsUrl } from '@/types/DaycareGroup'
import { useLazyQuery } from '@apollo/client'
import { GET_DAYCARE_GROUP_URLS } from '@/modules/API/daycareGroup/daycareGroupUrl'
import Loading from '@/components/Loading/Loading'
import ErrorMessage from '@/components/_custom/ErrorMessage/ErrorMessage'
import ButtonTextIcon from '@/components/_custom/Buttons/ButtonTextIcon/ButtonTextIcon'
import AddDaycareGroupUrlDialog from './Dialog/addDaycareGroupUrlDialog'
import Desktop from '@/modules/desktopMobile/desktop'
import { StyledDataGrid } from '@/components/_custom/DataGrid'
import Mobile from '@/modules/desktopMobile/mobile'
import { frFR } from '@mui/x-data-grid/locales'
import DaycareListDialog from '../../../groupList/Dialog/daycareListDialog'
import { Daycare } from '@/types/Daycare'
import { GridColDef, GridRowClassNameParams } from '@mui/x-data-grid'
import { HolidayVillage } from '@mui/icons-material'
import { Role } from '@/types/Role'
import { useAuth } from '@/modules/context/AuthContext'
import { getDaycareGroupsIconTextToJSX } from '@/modules/helper/iconsHelper'
import EditIcon from '@mui/icons-material/Edit'
import DeleteIcon from '@mui/icons-material/Delete'
import DeleteDaycareGroupUrlDialog from './Dialog/deleteDaycareGroupUrlDialog'
import EditDaycareGroupUrlDialog from './Dialog/editDaycareGroupUrlDialog'
import { useNotification } from '@/modules/context/NotificationContext.tsx'

interface GroupUrlsProps {
    group?: DaycareGroup
    canWrite: boolean
}

const GroupUrls: FunctionComponent<GroupUrlsProps> = ({ group, canWrite }: GroupUrlsProps) => {
    const { t } = useTranslation()

    const { user } = useAuth()

    const { showNotificationSuccess } = useNotification()

    const [isAddDaycareGroupUrlDialogOpen, setAddDaycareGroupUrlDialogOpen] = useState<boolean>(false)
    const [isDaycareListDialogOpen, setIsDaycareListDialogOpen] = useState<boolean>(false)
    const [isDeleteDaycareGroupUrlDialogOpen, setDeleteDaycareGroupUrlDialogOpen] = useState(false)
    const [isEditDaycareGroupUrlDialogOpen, setEditDaycareGroupUrlDialogOpen] = useState(false)

    const headerClassName: string = user?.role === Role.parent ? styles.HeaderParent : styles.HeaderDaycare

    const columnsWeb: GridColDef[] = [
        {
            field: 'structureCount',
            headerName: t('structureCount'),
            headerClassName: headerClassName,
            width: 150,
            maxWidth: 300,
            minWidth: 150,
            flex: 1,
            align: 'center',
            sortable: false,
            headerAlign: 'center',
            renderCell: (params) => (
                <div className={styles.DaycareListContainer} onClick={() => openDaycareList(params.row.daycareIds)}>
                    {params?.row.daycareIds ? params.row.daycareIds.length : 0}
                    <HolidayVillage className={styles.Icon} />
                </div>
            )
        },
        {
            field: 'icon',
            headerName: t('icon'),
            headerClassName: headerClassName,
            width: 200,
            maxWidth: 400,
            minWidth: 200,
            flex: 1,
            renderCell: (params) => (
                <div className={styles.IconContainer}>
                    {getDaycareGroupsIconTextToJSX(params.row.icon, styles.Icon)}
                </div>
            )
        },
        {
            field: 'name',
            headerName: t('name'),
            headerClassName: headerClassName,
            width: 200,
            maxWidth: 400,
            minWidth: 200,
            flex: 1,
            renderCell: (params) => <div className={styles.InfoContainer}>{params.row.text}</div>
        },
        {
            field: 'url',
            headerName: t('url'),
            headerClassName: headerClassName,
            width: 200,
            maxWidth: 400,
            minWidth: 200,
            flex: 1,
            renderCell: (params) => (
                <div className={styles.InfoContainer}>
                    <a className={styles.URL} href={params.row.url} target='_blank' rel='noreferrer'>
                        {params.row.url}
                    </a>
                </div>
            )
        },
        {
            field: 'actions',
            headerName: t('actions'),
            headerClassName: headerClassName,
            width: 200,
            maxWidth: 400,
            minWidth: 200,
            flex: 1,
            align: 'center',
            sortable: false,
            headerAlign: 'center',
            renderCell: (params) =>
                canWrite ? (
                    <div className={styles.DaycareListContainer}>
                        <div className={styles.DaycareListContainer}>
                            <EditIcon
                                onClick={() => handleSelectUrlById(params.row.id, 'edit')}
                                className={styles.Icon}
                            />
                            <DeleteIcon
                                onClick={() => handleSelectUrlById(params.row.id, 'delete')}
                                className={styles.Icon}
                            />
                        </div>
                    </div>
                ) : (
                    <></>
                )
        }
    ]
    const columnsMobile: GridColDef[] = [
        {
            field: 'structureCount',
            headerName: t('structureCount'),
            headerClassName: headerClassName,
            width: 50,
            maxWidth: 100,
            minWidth: 50,
            flex: 1,
            align: 'center',
            sortable: false,
            headerAlign: 'center',
            renderCell: (params) => (
                <div className={styles.DaycareListContainer} onClick={() => openDaycareList(params.row.daycareIds)}>
                    {params?.row.daycareIds ? params.row.daycareIds.length : 0}
                    <HolidayVillage className={styles.Icon} />
                </div>
            )
        },
        {
            field: 'url',
            headerName: t('url'),
            headerClassName: headerClassName,
            width: 100,
            minWidth: 100,
            maxWidth: 300,
            flex: 1,
            renderCell: (params) => (
                <div className={styles.InfoContainer}>
                    <a className={styles.URL} href={params.row.url} target='_blank' rel='noreferrer'>
                        {params.row.url}
                    </a>
                </div>
            )
        },
        {
            field: 'actions',
            headerName: t('actions'),
            headerClassName: headerClassName,
            width: 50,
            maxWidth: 100,
            minWidth: 50,
            flex: 1,
            align: 'center',
            sortable: false,
            headerAlign: 'center',
            renderCell: (params) =>
                canWrite ? (
                    <div className={styles.DaycareListContainer}>
                        <EditIcon onClick={() => handleSelectUrlById(params.row.id, 'edit')} className={styles.Icon} />
                        <DeleteIcon
                            onClick={() => handleSelectUrlById(params.row.id, 'delete')}
                            className={styles.Icon}
                        />
                    </div>
                ) : (
                    <></>
                )
        }
    ]

    const [showDaycares, setShowDaycares] = useState<Daycare[]>([])

    function openDaycareList(daycaresIds: number[]): void {
        if (daycaresIds && daycaresIds.length > 0) {
            const daycares: Daycare[] = group
                ? group.daycares.filter((daycare) => daycaresIds.includes(daycare.id))
                : []
            setShowDaycares(daycares)
            setIsDaycareListDialogOpen(true)
        } else {
            showNotificationSuccess(t('noDaycare'))
        }
    }

    const [daycareGroupUrls, setDaycareGroupUrls] = useState<Array<DaycareGroupsUrl>>([])

    const [getDaycareGroupUrls, { loading: loadingUrls, error: errorUrls, data: dataUrls, refetch: refetchUrls }] =
        useLazyQuery(GET_DAYCARE_GROUP_URLS, {
            variables: {
                groupId: group?.id
            }
        })

    useEffect(() => {
        if (dataUrls) {
            setDaycareGroupUrls(dataUrls.getDaycareGroupUrls)
        }
    }, [dataUrls])

    async function handleRefetchUrls(): Promise<void> {
        const { data } = await refetchUrls()
        if (data) {
            setDaycareGroupUrls([])
            setDaycareGroupUrls(data.getDaycareGroupUrls)
        }
    }

    useEffect(() => {
        getDaycareGroupUrls()
    }, [group])

    const [selectedUrl, setSelectedUrl] = useState<DaycareGroupsUrl | undefined>(undefined)

    function handleSelectUrlById(id: number, action: 'delete' | 'edit'): void {
        const url: DaycareGroupsUrl | undefined = daycareGroupUrls.find((url) => url.id === id)
        setSelectedUrl(url)
        if (action === 'delete') setDeleteDaycareGroupUrlDialogOpen(true)
        if (action === 'edit') setEditDaycareGroupUrlDialogOpen(true)
    }

    function handleCloseDialog(action: 'delete' | 'edit'): void {
        setSelectedUrl(undefined)

        if (action === 'delete') setDeleteDaycareGroupUrlDialogOpen(false)
        if (action === 'edit') setEditDaycareGroupUrlDialogOpen(false)
    }

    return (
        <div className={styles.Container}>
            <div className={styles.TitleAndButton}>
                <div className={styles.Title}>
                    <p>{t('yourUrls')}</p>
                </div>
                {canWrite && group && (
                    <>
                        <ButtonTextIcon
                            type={'add'}
                            onClick={() => setAddDaycareGroupUrlDialogOpen(true)}
                            className={styles.Button}
                        />
                        {isAddDaycareGroupUrlDialogOpen && (
                            <AddDaycareGroupUrlDialog
                                open={isAddDaycareGroupUrlDialogOpen}
                                group={group}
                                handleClose={() => setAddDaycareGroupUrlDialogOpen(false)}
                                refetchUrl={handleRefetchUrls}
                            />
                        )}
                    </>
                )}
            </div>
            {loadingUrls && <Loading overall={false} />}
            {dataUrls && (
                <>
                    <Desktop>
                        <StyledDataGrid
                            autoHeight
                            columns={columnsWeb}
                            rows={daycareGroupUrls}
                            disableDensitySelector
                            disableColumnMenu
                            sx={{ '--DataGrid-overlayHeight': '300px' }}
                            localeText={frFR.components.MuiDataGrid.defaultProps.localeText}
                            rowHeight={80}
                            className={styles.DataGrid}
                            getRowClassName={(params: GridRowClassNameParams): string => {
                                return `daycare-active-${params.row.active}`
                            }}
                        />
                    </Desktop>
                    <Mobile>
                        <StyledDataGrid
                            autoHeight
                            columns={columnsMobile}
                            rows={daycareGroupUrls}
                            disableDensitySelector
                            disableColumnMenu
                            sx={{ '--DataGrid-overlayHeight': '300px' }}
                            localeText={frFR.components.MuiDataGrid.defaultProps.localeText}
                            rowHeight={80}
                            className={styles.DataGrid}
                        />
                    </Mobile>
                    <DaycareListDialog
                        isDialogOpen={isDaycareListDialogOpen}
                        handleCloseDialog={() => setIsDaycareListDialogOpen(false)}
                        daycares={showDaycares}
                    />
                    {isDeleteDaycareGroupUrlDialogOpen && selectedUrl && (
                        <DeleteDaycareGroupUrlDialog
                            open={isDeleteDaycareGroupUrlDialogOpen}
                            handleClose={() => handleCloseDialog('delete')}
                            url={selectedUrl}
                            refetchUrl={handleRefetchUrls}
                        />
                    )}
                    {isEditDaycareGroupUrlDialogOpen && selectedUrl && group && (
                        <EditDaycareGroupUrlDialog
                            open={isEditDaycareGroupUrlDialogOpen}
                            handleClose={() => handleCloseDialog('edit')}
                            url={selectedUrl}
                            refetchUrl={handleRefetchUrls}
                            group={group}
                        />
                    )}
                </>
            )}
            {errorUrls && <ErrorMessage error={t('generalError')} />}
        </div>
    )
}

export default GroupUrls
